import {
  CollisionDetection,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { CellRef, DropTarget } from '../../../utils/dashboardBuilderLayout'

/**
 * The drag contract shared by the two surfaces that arrange widgets: the
 * template builder (#971) and the dashboard editor's edit mode.
 *
 * Both put the same two things on `active.data.current` — a widget type coming
 * out of the palette, or a reference to a widget already on the canvas — and
 * both put a `DropTarget` on `over.data.current`. That is the whole interface:
 * everything downstream is the pure layout math in `dashboardBuilderLayout.ts`,
 * so the two surfaces cannot drift apart in what a drop MEANS.
 */
export type LayoutDragData =
  | { source: 'palette'; widgetType: string }
  | { source: 'canvas'; ref: CellRef }

/** What a drop zone advertises to the DndContext handling the drag. */
export interface LayoutDropData {
  target: DropTarget
}

export function readDragData(data: unknown): LayoutDragData | undefined {
  const value = data as LayoutDragData | undefined
  if (value?.source === 'palette' || value?.source === 'canvas') return value
  return undefined
}

export function readDropTarget(data: unknown): DropTarget | undefined {
  return (data as LayoutDropData | undefined)?.target
}

/**
 * How a drop decides which zone it landed on (#1130).
 *
 * dnd-kit sizes the DragOverlay wrapper to the ACTIVATOR node, and for a
 * canvas move that is the 16px grab handle — so the collision rect is a tiny
 * box, and the default `rectIntersection` finds nothing unless it happens to
 * touch a 10px drop strip. Every other drop ended with `over: null`, the drag
 * was silently discarded, and the widget animated back to where it started —
 * the #1130 revert.
 *
 * Order of preference:
 * 1. `pointerWithin` — the pointer is the user's aim; a drop with the pointer
 *    inside a zone lands on exactly that zone, however the rects overlap.
 * 2. `rectIntersection` — keyboard drags have no pointer, and a touching rect
 *    is still an unambiguous aim.
 * 3. `closestCorners` — nothing under the pointer at all: land on the nearest
 *    boundary rather than throwing the drop away. Corners, not centre,
 *    because full-width row seams and thin cell gaps coexist and a wide
 *    zone's centre is a poor proxy for how near it is.
 */
export const layoutCollisionDetection: CollisionDetection = (args) => {
  const underPointer = pointerWithin(args)
  if (underPointer.length > 0) return underPointer
  const touching = rectIntersection(args)
  if (touching.length > 0) return touching
  return closestCorners(args)
}

/**
 * Sensors both surfaces use. The 4px activation distance keeps a click on a
 * widget's own controls from being read as a drag; the keyboard sensor is what
 * makes the palette and the canvas reachable without a pointer, now that drag
 * is the only way to place a widget.
 */
export function useLayoutDragSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  )
}
